/**
 * THE rate card. Every price, rate and threshold Homero charges lives here and
 * nowhere else.
 *
 * Before this file existed the same numbers were written out in five places —
 * `apps/api/src/constants/{pricing,config}.ts`, `apps/dashboard/constants/{booking,config}.ts`,
 * `apps/booking/src/constants/pricing.ts`, and inline in `apps/landing`'s
 * "Book from $X" button — and they had already drifted:
 *
 *   - the API dated V4 to 2025-04-13 and the dashboard to 2025-04-14, so every
 *     booking created in that 24-hour window was priced V4 by the server and
 *     displayed V3 by the admin UI;
 *   - the landing page still shipped the V2 table, so its advertised price and
 *     its `?t=<price>` handoff matched nothing downstream;
 *   - the customer's cart taxed at 8.88% while everything else used 8.875%.
 *
 * Those were not oversights, they were the predictable result of copying money
 * into five files. Add a rate here and every surface gets it at once.
 *
 * All amounts are integer cents.
 */

/**
 * Stable identifiers for the cleaning types.
 *
 * Historically the *price* was the identifier: the client sent
 * `cleaning_type: "128"` and the server recovered the name by switching on the
 * number against whichever table matched the booking date. That made price a
 * primary key, which means two cleaning types can never share a price, a stale
 * client bundle throws `invalid cleaning type`, and a decimal price matches
 * nothing at all. Ids are now identifiers and prices are data.
 */
export type CleaningTypeId =
  | "standard"
  | "special_mission"
  | "super_clean"
  | "move_in_out";

export const CLEANING_TYPE_IDS: CleaningTypeId[] = [
  "standard",
  "special_mission",
  "super_clean",
  "move_in_out",
];

/** Human-facing labels. The API's legacy wire format uses the spaced lowercase form. */
export const CLEANING_TYPE_LABELS: Record<CleaningTypeId, string> = {
  standard: "Standard",
  special_mission: "Special Mission",
  super_clean: "Super Clean",
  move_in_out: "Move In / Out",
};

/** The lowercase names used by the legacy API wire format and DB rows. */
export const CLEANING_TYPE_LEGACY_NAMES: Record<CleaningTypeId, string> = {
  standard: "standard",
  special_mission: "special mission",
  super_clean: "super clean",
  move_in_out: "move in / out",
};

export type CleaningTypePrices = Record<CleaningTypeId, number>;

/**
 * The flat room price every card charged before room prices were configurable,
 * in cents — per bedroom and per bathroom alike.
 *
 * Still what the legacy engine charges, and must stay so: a V1-V4 booking
 * reprices on the arithmetic it was sold under (see `legacy.ts`). It is also the
 * value every entry of `flatRoomPrices()` takes, which is what lets room pricing
 * ship without moving a price.
 */
export const ROOM_COST_CENTS = 2_000;

/**
 * The most bedrooms, and the most bathrooms, the booking funnel offers.
 *
 * The room tables below have exactly this many entries. A count past the end of
 * a table has no price to charge, so the engine refuses it rather than guessing
 * — `/bookings` already rejects more than five of either.
 */
export const MAX_ROOMS = 5;

/**
 * What each room adds, in cents: `bedroom[0]` is the first bedroom, `bedroom[1]`
 * the second, and so on. A studio has no bedroom and adds nothing for one.
 *
 * Marginal rather than cumulative, because that is the claim the business is
 * making — cleaning effort rises with every extra room, so the fourth bathroom
 * can cost more than the first. A cumulative table ("three bedrooms cost $80")
 * would say the same thing in a shape where a typo in one row silently changes
 * every larger home's price at once.
 */
export interface RoomPrices {
  bedroom: number[];
  bathroom: number[];
}

/** Every room at the same price — how every card priced rooms until now. */
export function flatRoomPrices(cents: number = ROOM_COST_CENTS): RoomPrices {
  const table = new Array<number>(MAX_ROOMS).fill(cents);
  return { bedroom: [...table], bathroom: [...table] };
}

export interface RateCard {
  /** Version label, for logs and admin display. */
  version: string;
  /** The instant this card takes effect. Bookings are priced by `created_at`. */
  effectiveFrom: Date;
  /** Base price per cleaning type, in cents. */
  cleaningTypes: CleaningTypePrices;
  /**
   * What each bedroom and bathroom adds to the base, in cents.
   *
   * On the rate card rather than beside it because a room price *is* a base
   * price: it is pinned to the booking's `created_at` exactly as the cleaning
   * type's price is, so editing a booking's pet instructions never reprices its
   * rooms against a newer table. Surge, Same-Day and every discount are
   * percentages of the base these produce, and follow them automatically.
   *
   * Read by the decimal engine only. The legacy engine charges `ROOM_COST_CENTS`
   * directly, and the V1-V4 cards carry the flat table only so every card has
   * the same shape.
   */
  roomCents: RoomPrices;
  /** Whether prices on this card may carry cents. */
  supportsDecimals: boolean;
}

/**
 * Version boundaries.
 *
 * V2-V4 are taken from `apps/api/src/constants/config.ts`, which is the
 * authoritative copy because it is the one that computed the charge. The
 * dashboard's V4 (2025-04-14) was a day late and is not reproduced here.
 */
export const V1_FROM = new Date(0);
export const V2_FROM = new Date("2022-07-20T13:16:12.346Z");
export const V3_FROM = new Date("2022-10-24T09:36:45.000Z");
export const V4_FROM = new Date("2025-04-13T08:52:00.000Z");

/**
 * Decimal pricing goes live at midnight 2026-07-26 in America/New_York.
 *
 * Homero operates in NYC, so the business day boundary is Eastern wall-clock
 * time. July is EDT (UTC-4), which puts the cutover at 04:00Z. Using a bare
 * UTC midnight would flip prices at 8pm ET on the 25th — customers would get
 * "tomorrow's" prices four hours early.
 */
export const V5_FROM = new Date("2026-08-02T04:00:00.000Z");

export const RATE_CARDS: RateCard[] = [
  {
    version: "V1",
    effectiveFrom: V1_FROM,
    supportsDecimals: false,
    roomCents: flatRoomPrices(),
    cleaningTypes: {
      standard: 8_800,
      special_mission: 13_800,
      super_clean: 22_800,
      move_in_out: 20_800,
    },
  },
  {
    version: "V2",
    effectiveFrom: V2_FROM,
    supportsDecimals: false,
    roomCents: flatRoomPrices(),
    cleaningTypes: {
      standard: 10_800,
      special_mission: 15_800,
      super_clean: 24_800,
      move_in_out: 22_800,
    },
  },
  {
    version: "V3",
    effectiveFrom: V3_FROM,
    supportsDecimals: false,
    roomCents: flatRoomPrices(),
    cleaningTypes: {
      standard: 10_800,
      special_mission: 15_800,
      super_clean: 26_300,
      move_in_out: 24_800,
    },
  },
  {
    version: "V4",
    effectiveFrom: V4_FROM,
    supportsDecimals: false,
    roomCents: flatRoomPrices(),
    cleaningTypes: {
      standard: 12_800,
      special_mission: 18_300,
      super_clean: 28_800,
      move_in_out: 27_300,
    },
  },
  {
    /**
     * V5 carries the V4 numbers deliberately.
     *
     * Phase 1 ships the *capability* to charge cents, not a new rate card. The
     * ~30% increase in the pricing overhaul doc is a conversion decision that
     * needs sign-off, and bundling it into this migration would make it
     * impossible to tell a rounding bug from an intended price change. Once the
     * new rates are signed off, editing the numbers below is the whole change.
     */
    version: "V5",
    effectiveFrom: V5_FROM,
    supportsDecimals: true,
    roomCents: flatRoomPrices(),
    cleaningTypes: {
      standard: 12_800,
      special_mission: 18_300,
      super_clean: 28_800,
      move_in_out: 27_300,
    },
  },
];

/** The rate card in force for a booking created at `createdAt`. */
export function getRateCard(createdAt: Date): RateCard {
  if (!(createdAt instanceof Date) || Number.isNaN(createdAt.getTime())) {
    throw new RangeError(
      `A valid created_at is required to select a rate card, got ${String(
        createdAt
      )}. ` +
        `Defaulting to the current time would reprice an existing booking.`
    );
  }
  let match = RATE_CARDS[0];
  for (const card of RATE_CARDS) {
    if (createdAt.getTime() >= card.effectiveFrom.getTime()) match = card;
  }
  return match;
}

/** Base price for a cleaning type, in cents, for the card in force at `createdAt`. */
export function getCleaningTypePriceCents(
  cleaningType: CleaningTypeId,
  createdAt: Date
): number {
  return getRateCard(createdAt).cleaningTypes[cleaningType];
}

/** Flat discount for accepting a flexible arrival window, in cents. */
export const FLEXIBLE_DISCOUNT_CENTS = 1_000;

/** Add-on prices, in cents. */
export const ADD_ON_COST_CENTS = {
  organizing: 4_000,
  oven: 3_500,
  fridge: 3_500,
  cabinets: 5_200,
  windows: 3_700,
  laundry: 3_700,
} as const;

/**
 * NYC combined sales tax, 8.875%, as an exact rational so it never becomes the
 * float 0.08875. The customer's cart previously used 8.88% here, which agreed
 * with the server only because integer subtotals hid the difference.
 */
export const TAX_RATE = { numerator: 8_875, denominator: 100_000 } as const;

export type Frequency =
  | "one time"
  | "weekly"
  | "every 2 weeks"
  | "every 3 weeks"
  | "every 4 weeks";

/** Frequency discount, as a whole percentage of the base. */
export const FREQUENCY_DISCOUNT_PERCENT: Record<Frequency, number> = {
  "one time": 0,
  weekly: 20,
  "every 2 weeks": 15,
  "every 3 weeks": 10,
  "every 4 weeks": 10,
};

/** Stripe billing interval count per frequency. */
export const FREQUENCY_INTERVAL_COUNT: Record<Frequency, number> = {
  "one time": 0,
  weekly: 1,
  "every 2 weeks": 2,
  "every 3 weeks": 3,
  "every 4 weeks": 4,
};

export function getFrequencyDiscountPercent(frequency: string): number {
  const key = frequency.toLowerCase() as Frequency;
  const percent = FREQUENCY_DISCOUNT_PERCENT[key];
  if (percent === undefined) throw new Error("Invalid frequency");
  return percent;
}

export function getFrequencyIntervalCount(frequency: string): number {
  const key = frequency.toLowerCase() as Frequency;
  const count = FREQUENCY_INTERVAL_COUNT[key];
  if (count === undefined) throw new Error("Invalid frequency");
  return count;
}

/** Whether a clean time string means the flexible 9am-4pm window. */
export function isFlexible(cleanTime: string | null | undefined): boolean {
  return (cleanTime || "").toLowerCase() === "flexible";
}
